<template lang="pug">
  #program-stats-modal.modal-card(style='width: auto')
    #loader(v-if="showLoader")
      BLoading(
        :active.sync="showLoader"
        :is-full-page="false"
      )
        .is-syncing-loader.has-text-weight-bold.is-size-1 Syncing..
    header.modal-card-head
      p.modal-card-title {{ program.name }} Details
      button.delete(type="button" @click="closeModal")
    section.modal-card-body
      ProgramHealth(:program="program")
      .box
        WtTitle Current Program Type: {{ programTypeLabel }}
        WtButton.is-justify-content-flex-end(v-if="!changeTypeSelectVisible" @click.native="showChangeTypeSelect") Change Program Type
        BField(label="Select Program Type" v-else)
          BSelect(
            placeholder="Choose Type"
            v-model="programType"
          )
            option(
              v-for="(type, index) in currentProgramTypes"
              :key="index"
              :value="type.value"
            )
              | {{ type.label }}
      .box
        WtTitle Program URL
        .columns
          .column
            BField(
              label="Current Program URL:"
              label-for=""
            )
              p {{ program.url }}
        .url-update-container
          BField(
            :type="{ 'is-danger': errors.has('program-url') }"
            :message="errors.first('program-url')"
          )
            UniquenessCheck(
              is-url
              label="Desired New URL:"
              placeholder="example"
              entity="programUrl"
              v-model="programUrl"
              name="program-url"
            )
          WtButton.url-submit-button(
            type="submit"
            color="red"
            @click.native="updateProgramUrl()"
          ) Submit

      .box
        WtTitle Contact Information
        .columns
          .column
            WtEntityTable(
              title="Administrators"
              :data="_.get(program, 'programAdmins', [])"
              default-sort-field="id"
              :is-striped="false"
            )
              BTableColumn(field="" label="Name" v-slot="column")
                | {{ _.get(column.row, "member.name", "") }}
              BTableColumn(field="" label="Email" v-slot="column")
                | {{ _.get(column.row, "member.email", "") }}
        .columns
          .column
            BField(
              v-if="program.primaryContact"
              label="Primary Contact"
              label-for="primary-contact"
            )
              BInput#primary-contact(
                disabled
                type="text"
                v-model="program.primaryContact"
              )
            BField(
              v-if="program.secondaryContact"
              label="Secondary Contact"
              label-for="secondary-contact"
            )
              BInput#secondary-contact(
                disabled
                type="text"
                v-model="program.secondaryContact"
              )
            BField(
              v-if="program.billingContact"
              label="Billing Contact"
              label-for="billing-contact"
            )
              BInput#billing-contact(
                disabled
                type="text"
                v-model="program.billingContact"
              )
            BField(
              v-if="program.decisionMaker"
              label="Decision Maker"
              label-for="decision-maker"
            )
              BInput#decision-maker(
                disabled
                type="text"
                v-model="program.decisionMaker"
              )
    footer.modal-card-foot
      .level.is-mobile
        .level-left
          .level-item
            WtButton(
              color="red"
              @click.native="zeroOutClick"
            ) Zero Out Program Data
        .level-right
          .level-item.button-gap
            WtButton(fullwidth @click.native="closeModal") Close
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { dateUtilityMixin } from "@/mixins/dateUtilityMixin";
import WtButton from "@@/WtButton/WtButton";
import WtSwitchQuestionInput from "@@/WtSwitchQuestionInput/WtSwitchQuestionInput";
import WtTitle from "@@/WtTitle/WtTitle";
import WtEntityTable from "@@/WtEntityTable/WtEntityTable";
import ProgramHealth from "@@/Program/ProgramHealth";
import UniquenessCheck from "@@/WtUniquenessCheckInput/WtUniquenessCheckInput";

export default {
  name: "ProgramStatsModal",

  components: {
    ProgramHealth,
    WtEntityTable,
    WtTitle,
    WtButton,
    WtSwitchQuestionInput,
    UniquenessCheck
  },

  mixins: [dateUtilityMixin],

  props: {
    program: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      cancelNowError: false,
      programSettingsError: false,
      changeTypeSelectVisible: true,
      programUrl: "",
      showLoader: false,
      currentProgramTypes: [
        {
          value: "bewellcore",
          label: "Be Well Core"
        },
        {
          value: "bewellplus",
          label: "Be Well Plus"
        },
        {
          value: "bewellpro",
          label: "Be Well Pro"
        }
      ],
      legacyToCurrentProgramTypeMappings: {
        activateproduct: "bewellcore",
        engageproduct: "bewellplus",
        thriveproduct: "bewellpro"
      }
    };
  },

  computed: {
    ...mapGetters("program", ["programSettingsCopy"]),

    programType: {
      async set(programType) {
        this.showLoader = true;
        try {
          const formData = {
            type: programType
          };

          const updateResponse = await this.updateProgramSettings({
            programId: this.program.id,
            settingType: "settings",
            formData
          });
          if (updateResponse.error) {
            this.snackBar({
              type: "is-danger",
              duration: 10000,
              queue: false,
              message: updateResponse.error.message
            });
          } else {
            this.updateProgramSettingsCopy({
              key: "type",
              value: programType
            });
            this.$set(this.program, "type", programType);
            this.snackBar({ message: "The program tier has been changed" });
          }
        } catch (error) {
          console.error(error);

          this.snackBar({
            type: "is-danger",
            duration: 10000,
            queue: false,
            message: `There was an error changing the program tier.
                          Please try again. Or please reach out to wellness@be-engaged.co.`
          });
        }
        this.changeTypeSelectVisible = false;
        this.showLoader = false;
      },
      get() {
        return this.getEquivalentProgramType(this.program.type);
      }
    },

    programTypeLabel() {
      const equivalentType = this.getEquivalentProgramType(this.program.type);
      return this._.get(
        this._.find([...this.currentProgramTypes], {
          value: equivalentType
        }),
        "label",
        ""
      );
    }
  },

  methods: {
    ...mapActions("program", [
      "updateProgramSettingsCopy",
      "updateProgramSettings"
    ]),
    ...mapActions(["snackBar"]),

    closeModal() {
      this.$emit("close");
    },

    getEquivalentProgramType(type) {
      return this.legacyToCurrentProgramTypeMappings[type] || type;
    },

    showChangeTypeSelect() {
      this.changeTypeSelectVisible = true;
    },

    updateProgramUrl() {
      const updateMessage = [
        "<div class='title is-4'>Update ",
        `${this.program.name}'s URL?`,
        "</div>",
        "Updating the program URL will effect all members associated with this program.",
        "<br /><br />",
        "This is a large impact change, and should ideally only be done once.",
        "<br /><br />",
        "<b>Are you sure you want to update ",
        `${this.program.name}`,
        "'s url?</b>"
      ].join(" ");

      this.$buefy.dialog.confirm({
        message: updateMessage,
        confirmText: "Update URL",
        type: "is-danger",
        onConfirm: async () => {
          const formData = new FormData();
          formData.set("url", this.programUrl);
          try {
            const updateResponse = await this.updateProgramSettings({
              programId: this.program.id,
              settingType: "settings",
              formData
            });
            if (updateResponse.error) {
              this.snackBar({
                type: "is-danger",
                duration: 10000,
                queue: false,
                message: updateResponse.error.message
              });
            } else {
              this.updateProgramSettingsCopy({
                key: "url",
                value: this.programUrl
              });
              this.snackBar({ message: "The program url has been changed" });
            }
          } catch (error) {
            this.snackBar({
              type: "is-danger",
              duration: 10000,
              queue: false,
              message: `There was an error changing the program url.
                        Please try again. Or please reach out to wellness@be-engaged.co.`
            });
          }
          return this._.get(this.programSettingsCopy, "url", "");
        }
      });
    },

    zeroOutClick() {
      this.$buefy.dialog.confirm({
        title: "Zero Out Program Data",
        confirmText: "Yes, Delete Everything",
        cancelText: "No, Cancel",
        type: "is-danger",
        message: `<strong style="color: red;">WARNING: This is NOT reversible!</strong><br><br>
                 This will delete below data from "<strong>${this.program.name}</strong>":<br>
                 • All non-admin members<br>
                 • All teams<br>
                 • All competitions/challenges<br><br>
                 Program admins and program settings will be preserved.`,
        closeOnConfirm: false,
        onConfirm: async (_, dialog) => {
          dialog.confirmText = "Processing ...";
          dialog.$refs.confirmButton.disabled = true;
          try {
            await this.axios.post(`super/programs/${this.program.id}/zero-out`);
            this.snackBar({
              message: "Zero-out scheduled. Data will be deleted soon."
            });
            this.$emit("close");
          } catch (error) {
            console.error("Error scheduling zero-out:", error);
            const message =
              error.response?.data?.message || "Failed to schedule zero-out";
            this.snackBar({ message, type: "is-danger" });
          }
          dialog.close();
        }
      });
    }
  }
};
</script>

<style lang="sass" scoped>
.button-gap
  margin-left: 1rem

.url-update-container
  display: flex
  align-items: flex-end
  gap: 1rem

  .url-submit-button
    position: relative
    margin-bottom: 0.75rem
</style>
